import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  ContentCopy,
  CheckCircle,
  Cancel,
  Api,
  Search,
  Public,
  Lock,
  AdminPanelSettings,
} from '@mui/icons-material';
import api from '../../../services/api';

interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string;
  description: string;
  auth: 'none' | 'user' | 'admin';
  params?: { name: string; type: string; required: boolean; description: string }[];
  body?: { name: string; type: string; required: boolean; description: string }[];
  response?: string;
}

interface ApiGroup {
  name: string;
  description: string;
  basePath: string;
  endpoints: ApiEndpoint[];
}

const apiGroups: ApiGroup[] = [
  {
    name: 'Autenticação',
    description: 'Endpoints para autenticação e gestão de sessões',
    basePath: '/v1/auth',
    endpoints: [
      {
        method: 'POST',
        path: '/login',
        description: 'Autenticação de utilizador',
        auth: 'none',
        body: [
          { name: 'email', type: 'string', required: true, description: 'Email do utilizador' },
          { name: 'password', type: 'string', required: true, description: 'Senha do utilizador' },
        ],
        response: '{ success: true, token: "jwt...", user: {...} }',
      },
      {
        method: 'POST',
        path: '/register',
        description: 'Registo de novo utilizador',
        auth: 'none',
        body: [
          { name: 'email', type: 'string', required: true, description: 'Email do utilizador' },
          { name: 'password', type: 'string', required: true, description: 'Senha do utilizador' },
          { name: 'name', type: 'string', required: true, description: 'Nome completo' },
          { name: 'phone', type: 'string', required: false, description: 'Telefone' },
        ],
      },
      {
        method: 'POST',
        path: '/logout',
        description: 'Terminar sessão',
        auth: 'user',
      },
      {
        method: 'POST',
        path: '/refresh',
        description: 'Renovar token de autenticação',
        auth: 'user',
      },
      {
        method: 'POST',
        path: '/reset-password',
        description: 'Solicitar recuperação de senha',
        auth: 'none',
        body: [
          { name: 'email', type: 'string', required: true, description: 'Email do utilizador' },
        ],
        response: '{ success: true, message: "Se o email existir, receberá instruções de recuperação" }',
      },
      {
        method: 'POST',
        path: '/reset-password/confirm',
        description: 'Confirmar nova senha com token',
        auth: 'none',
        body: [
          { name: 'token', type: 'string', required: true, description: 'Token de reset recebido por email' },
          { name: 'newPassword', type: 'string', required: true, description: 'Nova senha (mínimo 8 caracteres)' },
        ],
        response: '{ success: true, message: "Senha alterada com sucesso" }',
      },
      {
        method: 'POST',
        path: '/verify',
        description: 'Verificar token e obter dados do utilizador',
        auth: 'none',
        body: [
          { name: 'token', type: 'string', required: true, description: 'Token Firebase ID' },
        ],
        response: '{ success: true, user: {...} }',
      },
    ],
  },
  {
    name: 'Serviços',
    description: 'Gestão de serviços disponíveis na plataforma',
    basePath: '/v1/admin/services',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista todos os serviços com paginação e filtros',
        auth: 'admin',
        params: [
          { name: 'page', type: 'number', required: false, description: 'Página (default: 1)' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página (default: 10)' },
          { name: 'search', type: 'string', required: false, description: 'Pesquisa por nome, código ou descrição' },
          { name: 'status', type: 'string', required: false, description: 'Filtrar por estado' },
          { name: 'type', type: 'string', required: false, description: 'Filtrar por tipo' },
        ],
      },
      {
        method: 'GET',
        path: '/types',
        description: 'Lista tipos de serviço activos',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes de um serviço específico',
        auth: 'admin',
        params: [
          { name: 'id', type: 'number', required: true, description: 'ID do serviço' },
        ],
      },
      {
        method: 'POST',
        path: '/',
        description: 'Criar novo serviço',
        auth: 'admin',
        body: [
          { name: 'code', type: 'string', required: true, description: 'Código único do serviço' },
          { name: 'name', type: 'string', required: true, description: 'Nome do serviço' },
          { name: 'description', type: 'string', required: false, description: 'Descrição' },
          { name: 'value', type: 'number', required: false, description: 'Valor fixo' },
          { name: 'minValue', type: 'number', required: false, description: 'Valor mínimo' },
          { name: 'maxValue', type: 'number', required: false, description: 'Valor máximo' },
          { name: 'currency', type: 'string', required: false, description: 'Moeda (default: CVE)' },
          { name: 'typeId', type: 'number', required: false, description: 'ID do tipo de serviço' },
          { name: 'entityId', type: 'number', required: true, description: 'ID da entidade' },
          { name: 'allowCustomValue', type: 'boolean', required: false, description: 'Permite valor livre' },
          { name: 'requiresReference', type: 'boolean', required: false, description: 'Requer referência' },
        ],
      },
      {
        method: 'PUT',
        path: '/:id',
        description: 'Atualizar serviço existente',
        auth: 'admin',
      },
      {
        method: 'DELETE',
        path: '/:id',
        description: 'Eliminar serviço (soft delete)',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/types',
        description: 'Criar tipo de serviço',
        auth: 'admin',
        body: [
          { name: 'code', type: 'string', required: true, description: 'Código único' },
          { name: 'name', type: 'string', required: true, description: 'Nome do tipo' },
          { name: 'description', type: 'string', required: false, description: 'Descrição' },
        ],
      },
      {
        method: 'PUT',
        path: '/types/:id',
        description: 'Atualizar tipo de serviço',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Parametrizações',
    description: 'Configurações e parâmetros do sistema',
    basePath: '/v1/admin/parametrizacoes',
    endpoints: [
      {
        method: 'GET',
        path: '/status',
        description: 'Verifica estado das tabelas de parametrização',
        auth: 'admin',
        response: '{ success: true, tables: { bancos: true, sms_intervals: true, ... } }',
      },
      {
        method: 'POST',
        path: '/migrate',
        description: 'Cria tabelas em falta com dados de exemplo',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/bancos',
        description: 'Lista todos os bancos',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/bancos',
        description: 'Criar novo banco',
        auth: 'admin',
        body: [
          { name: 'codigo', type: 'string', required: true, description: 'Código do banco' },
          { name: 'nome', type: 'string', required: true, description: 'Nome do banco' },
          { name: 'swift', type: 'string', required: false, description: 'Código SWIFT' },
        ],
      },
      {
        method: 'PUT',
        path: '/bancos/:id',
        description: 'Atualizar banco',
        auth: 'admin',
      },
      {
        method: 'DELETE',
        path: '/bancos/:id',
        description: 'Eliminar banco',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/sms-intervals',
        description: 'Lista intervalos de SMS',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/sms-intervals',
        description: 'Criar intervalo SMS',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/languages',
        description: 'Lista línguas disponíveis',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/languages',
        description: 'Criar nova língua',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/pos-sisp',
        description: 'Lista terminais POS SISP',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/pos-sisp',
        description: 'Criar terminal POS',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/sisp-tokenization',
        description: 'Lista configurações de tokenização SISP',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/sisp-tokenization',
        description: 'Criar configuração de tokenização',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/sisp-entities',
        description: 'Lista entidades SISP',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/sisp-entities',
        description: 'Criar entidade SISP',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/sisp-services',
        description: 'Lista associações de serviços SISP',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/sisp-services',
        description: 'Criar associação de serviço SISP',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Utilizadores',
    description: 'Gestão de utilizadores do sistema',
    basePath: '/v1/admin/users',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista utilizadores com paginação',
        auth: 'admin',
        params: [
          { name: 'page', type: 'number', required: false, description: 'Página' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página' },
          { name: 'search', type: 'string', required: false, description: 'Pesquisa' },
          { name: 'status', type: 'string', required: false, description: 'Estado' },
        ],
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes do utilizador',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/',
        description: 'Criar utilizador',
        auth: 'admin',
      },
      {
        method: 'PUT',
        path: '/:id',
        description: 'Atualizar utilizador',
        auth: 'admin',
      },
      {
        method: 'DELETE',
        path: '/:id',
        description: 'Eliminar utilizador',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Clientes',
    description: 'Gestão de clientes',
    basePath: '/v1/admin/customers',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista clientes',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes do cliente',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/',
        description: 'Criar cliente',
        auth: 'admin',
      },
      {
        method: 'PUT',
        path: '/:id',
        description: 'Atualizar cliente',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Entidades',
    description: 'Gestão de entidades parceiras',
    basePath: '/v1/admin/entities',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista entidades',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes da entidade',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/',
        description: 'Criar entidade',
        auth: 'admin',
      },
      {
        method: 'PUT',
        path: '/:id',
        description: 'Atualizar entidade',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Pagamentos',
    description: 'Gestão de pagamentos',
    basePath: '/v1/admin/payments',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista pagamentos',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes do pagamento',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/:id/refund',
        description: 'Reembolsar pagamento',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'SISP (Vinti4)',
    description: 'Gateway de pagamentos SISP - Pagamentos, Tokenização e Estornos',
    basePath: '/v1/admin/sisp',
    endpoints: [
      {
        method: 'GET',
        path: '/payments',
        description: 'Lista pagamentos SISP com filtros e paginação',
        auth: 'admin',
        params: [
          { name: 'page', type: 'number', required: false, description: 'Página (default: 1)' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página (default: 10)' },
          { name: 'status', type: 'string', required: false, description: 'Filtrar por estado (S=Submetido, N=Pago, F=Falhou, E=Estornado)' },
          { name: 'entidadeId', type: 'string', required: false, description: 'Filtrar por entidade' },
          { name: 'startDate', type: 'string', required: false, description: 'Data início (YYYY-MM-DD)' },
          { name: 'endDate', type: 'string', required: false, description: 'Data fim (YYYY-MM-DD)' },
          { name: 'origem', type: 'string', required: false, description: 'Origem do pagamento (WEBS, RCG, EVT, etc)' },
          { name: 'search', type: 'string', required: false, description: 'Pesquisa por referência ou cliente' },
        ],
        response: '{ success: true, data: [...], pagination: { page, limit, total, totalPages } }',
      },
      {
        method: 'GET',
        path: '/payments/:id',
        description: 'Detalhes de um pagamento SISP específico',
        auth: 'admin',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do pagamento' },
        ],
        response: '{ success: true, data: { ...payment, responses: [...], refunds: [...] } }',
      },
      {
        method: 'GET',
        path: '/payments/stats',
        description: 'Estatísticas de pagamentos SISP',
        auth: 'admin',
        params: [
          { name: 'period', type: 'number', required: false, description: 'Período em dias (default: 30)' },
        ],
        response: '{ success: true, data: { summary: {...}, dailyPayments: [...], methodStats: [...], originStats: [...] } }',
      },
      {
        method: 'POST',
        path: '/refund',
        description: 'Solicitar estorno de um pagamento',
        auth: 'admin',
        body: [
          { name: 'pedidoId', type: 'string', required: true, description: 'ID do pedido a estornar' },
          { name: 'motivo', type: 'string', required: false, description: 'Motivo do estorno' },
        ],
        response: '{ success: true, data: { refundId, merchantRef }, message: "..." }',
      },
      {
        method: 'GET',
        path: '/tokens',
        description: 'Lista tokens de cartões guardados dos clientes',
        auth: 'admin',
        params: [
          { name: 'page', type: 'number', required: false, description: 'Página' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página' },
          { name: 'clientId', type: 'string', required: false, description: 'Filtrar por cliente' },
          { name: 'activo', type: 'string', required: false, description: 'Filtrar por estado (S/N)' },
          { name: 'search', type: 'string', required: false, description: 'Pesquisa' },
        ],
        response: '{ success: true, data: [...], pagination: {...} }',
      },
      {
        method: 'POST',
        path: '/tokens',
        description: 'Iniciar processo de tokenização (guardar cartão)',
        auth: 'user',
        body: [
          { name: 'posId', type: 'string', required: true, description: 'ID do terminal POS' },
          { name: 'maxTransactions', type: 'number', required: false, description: 'Máximo de transações permitidas' },
          { name: 'maxAmount', type: 'number', required: false, description: 'Montante máximo por transação' },
        ],
        response: '{ success: true, data: { id, merchantRef, merchantSession, redirectUrl } }',
      },
      {
        method: 'POST',
        path: '/tokens/callback',
        description: 'Callback público para resposta da tokenização SISP',
        auth: 'none',
        body: [
          { name: 'messageType', type: 'string', required: true, description: 'Tipo de mensagem (8=tokenização)' },
          { name: 'merchantRef', type: 'string', required: true, description: 'Referência do merchant' },
          { name: 'token', type: 'string', required: true, description: 'Token gerado pelo SISP' },
          { name: 'cardLastFour', type: 'string', required: false, description: 'Últimos 4 dígitos do cartão' },
          { name: 'cardType', type: 'string', required: false, description: 'Tipo do cartão (VISA, MC, VINTI4)' },
        ],
      },
      {
        method: 'DELETE',
        path: '/tokens/:id',
        description: 'Desativar um token de cartão',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do token' },
        ],
        response: '{ success: true, message: "Token desativado com sucesso" }',
      },
      {
        method: 'POST',
        path: '/payments/token',
        description: 'Efetuar pagamento com cartão guardado (token)',
        auth: 'user',
        body: [
          { name: 'tokenId', type: 'string', required: true, description: 'ID do token do cartão' },
          { name: 'facturaEntidadeId', type: 'string', required: true, description: 'ID da fatura a pagar' },
          { name: 'amount', type: 'number', required: true, description: 'Montante a pagar' },
          { name: 'origem', type: 'string', required: true, description: 'Origem do pagamento' },
          { name: 'entityCode', type: 'string', required: false, description: 'Código da entidade' },
          { name: 'referenceNumber', type: 'string', required: false, description: 'Número de referência' },
          { name: 'entidadeId', type: 'string', required: true, description: 'ID da entidade' },
        ],
        response: '{ success: true, message: "...", data: { paymentId, status } }',
      },
      {
        method: 'GET',
        path: '/pos',
        description: 'Lista terminais POS SISP configurados',
        auth: 'admin',
        params: [
          { name: 'activo', type: 'string', required: false, description: 'Filtrar por estado (S/N)' },
        ],
        response: '{ success: true, data: [...] }',
      },
      {
        method: 'GET',
        path: '/entities',
        description: 'Lista entidades de pagamento SISP',
        auth: 'admin',
        params: [
          { name: 'activo', type: 'string', required: false, description: 'Filtrar por estado' },
          { name: 'grupo', type: 'string', required: false, description: 'Filtrar por grupo' },
        ],
        response: '{ success: true, data: [...] }',
      },
      {
        method: 'GET',
        path: '/logs',
        description: 'Lista logs de comunicação com SISP',
        auth: 'admin',
        params: [
          { name: 'page', type: 'number', required: false, description: 'Página' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página' },
          { name: 'pedidoId', type: 'string', required: false, description: 'Filtrar por pedido' },
          { name: 'tipo', type: 'string', required: false, description: 'Tipo de log' },
          { name: 'startDate', type: 'string', required: false, description: 'Data início' },
          { name: 'endDate', type: 'string', required: false, description: 'Data fim' },
        ],
        response: '{ success: true, data: [...], pagination: {...} }',
      },
      {
        method: 'GET',
        path: '/logs/:id',
        description: 'Detalhes de um log específico',
        auth: 'admin',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do log' },
        ],
      },
    ],
  },
  {
    name: 'Faturas',
    description: 'Gestão de faturas',
    basePath: '/v1/admin/invoices',
    endpoints: [
      {
        method: 'GET',
        path: '/',
        description: 'Lista faturas',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id',
        description: 'Detalhes da fatura',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/:id/pdf',
        description: 'Download PDF da fatura',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Dashboard',
    description: 'Estatísticas e métricas',
    basePath: '/v1/admin/dashboard',
    endpoints: [
      {
        method: 'GET',
        path: '/stats',
        description: 'Estatísticas gerais',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/revenue',
        description: 'Dados de receita',
        auth: 'admin',
        params: [
          { name: 'period', type: 'string', required: false, description: 'Período (day, week, month, year)' },
        ],
      },
      {
        method: 'GET',
        path: '/transactions',
        description: 'Resumo de transações',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'PagaliShop',
    description: 'E-commerce e loja online',
    basePath: '/v1/admin/shop',
    endpoints: [
      {
        method: 'GET',
        path: '/products',
        description: 'Lista produtos',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/products',
        description: 'Criar produto',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/orders',
        description: 'Lista pedidos',
        auth: 'admin',
      },
      {
        method: 'PUT',
        path: '/orders/:id/status',
        description: 'Atualizar estado do pedido',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/inventory',
        description: 'Lista estoque',
        auth: 'admin',
      },
      {
        method: 'GET',
        path: '/promotions',
        description: 'Lista promoções',
        auth: 'admin',
      },
      {
        method: 'POST',
        path: '/promotions',
        description: 'Criar promoção',
        auth: 'admin',
      },
    ],
  },
  {
    name: 'Público',
    description: 'Endpoints públicos (sem autenticação)',
    basePath: '/v1/public',
    endpoints: [
      {
        method: 'GET',
        path: '/servicos/tipos',
        description: 'Lista tipos de serviços',
        auth: 'none',
        response: '{ success: true, data: [{ id, nome, descricao, codigo, icone }] }',
      },
      {
        method: 'GET',
        path: '/servicos/categorias',
        description: 'Lista categorias de serviços',
        auth: 'none',
        response: '{ success: true, data: [{ id, nome, descricao, icone }] }',
      },
      {
        method: 'GET',
        path: '/servicos',
        description: 'Lista serviços disponíveis',
        auth: 'none',
        params: [
          { name: 'categoria', type: 'string', required: false, description: 'Filtrar por categoria' },
        ],
        response: '{ success: true, data: [...] }',
      },
      {
        method: 'GET',
        path: '/servicos/:id',
        description: 'Detalhes de um serviço',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do serviço' },
        ],
      },
      {
        method: 'GET',
        path: '/eventos',
        description: 'Lista eventos ativos',
        auth: 'none',
        response: '{ success: true, data: [{ id, nome, dataEvento, local, preco, ... }] }',
      },
      {
        method: 'GET',
        path: '/eventos/:id',
        description: 'Detalhes de um evento',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do evento' },
        ],
      },
      {
        method: 'GET',
        path: '/donativos',
        description: 'Lista campanhas de donativos',
        auth: 'none',
        response: '{ success: true, data: [{ id, nome, meta, totalArrecadado, ... }] }',
      },
      {
        method: 'GET',
        path: '/donativos/:id',
        description: 'Detalhes de uma campanha de donativo',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do donativo' },
        ],
        response: '{ success: true, data: { ...donativo, progresso } }',
      },
      {
        method: 'GET',
        path: '/lojas/tipos',
        description: 'Lista tipos de lojas',
        auth: 'none',
      },
      {
        method: 'GET',
        path: '/lojas',
        description: 'Lista lojas disponíveis',
        auth: 'none',
        params: [
          { name: 'tipo', type: 'string', required: false, description: 'Filtrar por tipo' },
        ],
      },
      {
        method: 'GET',
        path: '/lojas/:id',
        description: 'Detalhes de uma loja',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da loja' },
        ],
      },
      {
        method: 'GET',
        path: '/lojas/:id/categorias',
        description: 'Lista categorias de produtos de uma loja',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da loja' },
        ],
      },
      {
        method: 'GET',
        path: '/lojas/:id/produtos',
        description: 'Lista produtos de uma loja',
        auth: 'none',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da loja' },
          { name: 'categoria', type: 'string', required: false, description: 'Filtrar por categoria' },
        ],
      },
      {
        method: 'GET',
        path: '/publicidade',
        description: 'Lista spots publicitários',
        auth: 'none',
        params: [
          { name: 'codigo', type: 'string', required: false, description: 'Filtrar por código' },
        ],
      },
      {
        method: 'GET',
        path: '/paises',
        description: 'Lista países disponíveis',
        auth: 'none',
      },
      {
        method: 'GET',
        path: '/tipos-cliente',
        description: 'Lista tipos de cliente para registo',
        auth: 'none',
      },
    ],
  },
  {
    name: 'Cliente',
    description: 'Endpoints para clientes autenticados',
    basePath: '/v1/client',
    endpoints: [
      {
        method: 'GET',
        path: '/dashboard',
        description: 'Dashboard do cliente com transações recentes e estatísticas',
        auth: 'user',
        response: '{ success: true, data: { cliente, transacoesRecentes, totalMes, notificacoesNaoLidas } }',
      },
      {
        method: 'GET',
        path: '/profile',
        description: 'Perfil completo do cliente',
        auth: 'user',
        response: '{ success: true, data: { id, nome, email, avatar, ... } }',
      },
      {
        method: 'PUT',
        path: '/profile',
        description: 'Atualizar perfil do cliente',
        auth: 'user',
        body: [
          { name: 'nome', type: 'string', required: false, description: 'Nome completo' },
          { name: 'morada', type: 'string', required: false, description: 'Morada' },
          { name: 'dataNascimento', type: 'string', required: false, description: 'Data de nascimento' },
          { name: 'email', type: 'string', required: false, description: 'Email' },
        ],
      },
      {
        method: 'DELETE',
        path: '/profile',
        description: 'Desativar conta do cliente',
        auth: 'user',
        response: '{ success: true, message: "Conta desativada com sucesso" }',
      },
      {
        method: 'POST',
        path: '/profile/avatar',
        description: 'Atualizar avatar do cliente',
        auth: 'user',
        body: [
          { name: 'avatar', type: 'string', required: true, description: 'Imagem em base64' },
          { name: 'mimeType', type: 'string', required: false, description: 'Tipo MIME (default: image/png)' },
        ],
      },
      {
        method: 'PUT',
        path: '/profile/password',
        description: 'Alterar senha do utilizador',
        auth: 'user',
        body: [
          { name: 'senhaAtual', type: 'string', required: true, description: 'Senha atual' },
          { name: 'novaSenha', type: 'string', required: true, description: 'Nova senha (mínimo 8 caracteres)' },
        ],
        response: '{ success: true, message: "Senha alterada com sucesso" }',
      },
      {
        method: 'GET',
        path: '/wallet',
        description: 'Lista cartões guardados (tokens)',
        auth: 'user',
        response: '{ success: true, data: [{ id, token, pan, descricao, corTema, ... }] }',
      },
      {
        method: 'GET',
        path: '/wallet/:id',
        description: 'Detalhes de um cartão específico',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do cartão' },
        ],
        response: '{ success: true, data: { id, token, pan, maxTransacoes, maxValor, tipoCartao, titular, ... } }',
      },
      {
        method: 'GET',
        path: '/wallet/:id/history',
        description: 'Histórico de transações de um cartão',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do cartão' },
          { name: 'startDate', type: 'string', required: false, description: 'Data início' },
          { name: 'endDate', type: 'string', required: false, description: 'Data fim' },
        ],
        response: '{ success: true, data: [{ id, merchantRef, valor, data, estado, entidade, ... }] }',
      },
      {
        method: 'PUT',
        path: '/wallet/:id/theme',
        description: 'Atualizar cor do tema do cartão',
        auth: 'user',
        body: [
          { name: 'color', type: 'string', required: true, description: 'Cor em hex (ex: #28CAC0)' },
        ],
      },
      {
        method: 'GET',
        path: '/transactions',
        description: 'Lista transações com filtros e paginação',
        auth: 'user',
        params: [
          { name: 'startDate', type: 'string', required: false, description: 'Data início' },
          { name: 'endDate', type: 'string', required: false, description: 'Data fim' },
          { name: 'limit', type: 'number', required: false, description: 'Itens por página (default: 50)' },
          { name: 'offset', type: 'number', required: false, description: 'Offset' },
        ],
        response: '{ success: true, data: [...], pagination: { total, limit, offset } }',
      },
      {
        method: 'GET',
        path: '/transactions/:id',
        description: 'Detalhes de uma transação específica',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da transação' },
        ],
        response: '{ success: true, data: { id, valor, referencia, estado, entidade, servico, pagamento: {...} } }',
      },
      {
        method: 'GET',
        path: '/transactions/:id/receipt',
        description: 'Recibo de uma transação',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da transação' },
        ],
        response: '{ success: true, data: { reciboId, transacao, cliente, entidade, servico, pagamento, reciboSisp } }',
      },
      {
        method: 'GET',
        path: '/despesas/:mes/:ano',
        description: 'Despesas mensais por dia',
        auth: 'user',
        params: [
          { name: 'mes', type: 'number', required: true, description: 'Mês (1-12)' },
          { name: 'ano', type: 'number', required: true, description: 'Ano' },
        ],
        response: '{ success: true, data: { despesas: [...], totalMes } }',
      },
      {
        method: 'GET',
        path: '/notifications',
        description: 'Lista notificações do cliente',
        auth: 'user',
        response: '{ success: true, data: [{ id, titulo, mensagem, data, lida, tipo }] }',
      },
      {
        method: 'GET',
        path: '/notifications/:id',
        description: 'Detalhes de uma notificação (marca como lida automaticamente)',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da notificação' },
        ],
        response: '{ success: true, data: { id, titulo, mensagem, urlAcao, dadosExtra, ... } }',
      },
      {
        method: 'PUT',
        path: '/notifications/:id/read',
        description: 'Marcar notificação como lida',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID da notificação' },
        ],
      },
      {
        method: 'PUT',
        path: '/notifications/read-all',
        description: 'Marcar todas notificações como lidas',
        auth: 'user',
        response: '{ success: true, message: "Todas as notificações foram marcadas como lidas" }',
      },
      {
        method: 'GET',
        path: '/orders',
        description: 'Lista pedidos na loja',
        auth: 'user',
        response: '{ success: true, data: [{ id, dataRegisto, estado, loja, total, referencia }] }',
      },
      {
        method: 'GET',
        path: '/orders/:id',
        description: 'Detalhes de um pedido',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do pedido' },
        ],
        response: '{ success: true, data: { id, estado, loja: {...}, factura: {...}, itens: [...] } }',
      },
      {
        method: 'GET',
        path: '/tickets',
        description: 'Lista bilhetes de eventos do cliente',
        auth: 'user',
        response: '{ success: true, data: [{ id, codigo, qrCode, estado, evento: {...}, entidade }] }',
      },
      {
        method: 'GET',
        path: '/tickets/:id',
        description: 'Detalhes de um bilhete',
        auth: 'user',
        params: [
          { name: 'id', type: 'string', required: true, description: 'ID do bilhete' },
        ],
        response: '{ success: true, data: { id, codigo, qrCode, tipoBilhete, preco, evento: {...}, entidade: {...}, factura: {...} } }',
      },
    ],
  },
];

const methodColors: Record<string, string> = {
  GET: '#61affe',
  POST: '#49cc90',
  PUT: '#fca130',
  DELETE: '#f93e3e',
  PATCH: '#50e3c2',
};

const ApiDocumentation: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'loading' | 'online' | 'offline'>('loading');
  const [baseUrl, setBaseUrl] = useState('');

  useEffect(() => {
    // Check API status
    const checkApiStatus = async () => {
      try {
        const response = await api.get('/health');
        if (response.data?.status === 'ok') {
          setApiStatus('online');
        }
      } catch {
        setApiStatus('offline');
      }
    };
    checkApiStatus();

    // Get base URL from environment
    setBaseUrl(process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api');
  }, []);

  const handleCopyPath = (fullPath: string) => {
    navigator.clipboard.writeText(baseUrl + fullPath);
    setCopiedPath(fullPath);
    setTimeout(() => setCopiedPath(null), 2000);
  };

  const filteredGroups = apiGroups.filter(group => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      group.name.toLowerCase().includes(search) ||
      group.description.toLowerCase().includes(search) ||
      group.endpoints.some(
        e => e.path.toLowerCase().includes(search) || e.description.toLowerCase().includes(search)
      )
    );
  });

  const getAuthIcon = (auth: string) => {
    switch (auth) {
      case 'none':
        return <Public fontSize="small" color="success" />;
      case 'user':
        return <Lock fontSize="small" color="warning" />;
      case 'admin':
        return <AdminPanelSettings fontSize="small" color="error" />;
      default:
        return null;
    }
  };

  const getAuthLabel = (auth: string) => {
    switch (auth) {
      case 'none':
        return t('configApi.public');
      case 'user':
        return t('configApi.user');
      case 'admin':
        return t('configApi.admin');
      default:
        return auth;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('configApi.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('configApi.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={apiStatus === 'loading' ? <CircularProgress size={16} /> : apiStatus === 'online' ? <CheckCircle /> : <Cancel />}
            label={apiStatus === 'loading' ? t('configApi.checking') : apiStatus === 'online' ? t('configApi.apiOnline') : t('configApi.apiOffline')}
            color={apiStatus === 'online' ? 'success' : apiStatus === 'offline' ? 'error' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* API Info Card */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('configApi.baseUrl')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" fontFamily="monospace" sx={{ bgcolor: 'action.hover', px: 2, py: 1, borderRadius: 1 }}>
                    {baseUrl}
                  </Typography>
                  <Tooltip title={t('configApi.copy')}>
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(baseUrl)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('configApi.authentication')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip icon={<Public />} label={t('configApi.publicNoAuth')} size="small" color="success" variant="outlined" />
                  <Chip icon={<Lock />} label={t('configApi.userJwt')} size="small" color="warning" variant="outlined" />
                  <Chip icon={<AdminPanelSettings />} label={t('configApi.adminJwtRole')} size="small" color="error" variant="outlined" />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Search and Tabs */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ mb: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}>
              <TextField
                fullWidth
                placeholder={t('configApi.searchEndpoints')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label={t('configApi.all')} />
                <Tab label={t('configApi.admin')} />
                <Tab label={t('configApi.client')} />
                <Tab label={t('configApi.public')} />
              </Tabs>
            </Box>
          </Paper>
        </Grid>

        {/* API Groups */}
        <Grid size={{ xs: 12 }}>
          {filteredGroups
            .filter(group => {
              if (activeTab === 0) return true;
              if (activeTab === 1) return group.basePath.includes('/admin/');
              if (activeTab === 2) return group.basePath.includes('/client');
              if (activeTab === 3) return group.basePath.includes('/public') || group.endpoints.some(e => e.auth === 'none');
              return true;
            })
            .map((group) => (
              <Accordion key={group.name} defaultExpanded={filteredGroups.length <= 3}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Api color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {group.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.basePath} - {group.description}
                      </Typography>
                    </Box>
                    <Chip label={`${group.endpoints.length} ${t('configApi.endpoints')}`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={100}>{t('configApi.method')}</TableCell>
                          <TableCell>{t('configApi.endpoint')}</TableCell>
                          <TableCell>{t('common.description')}</TableCell>
                          <TableCell width={100}>Auth</TableCell>
                          <TableCell width={50}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.endpoints.map((endpoint, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Chip
                                label={endpoint.method}
                                size="small"
                                sx={{
                                  bgcolor: methodColors[endpoint.method],
                                  color: 'white',
                                  fontWeight: 600,
                                  minWidth: 65,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {group.basePath}{endpoint.path}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{endpoint.description}</Typography>
                              {endpoint.params && endpoint.params.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('configApi.parameters')}: {endpoint.params.map(p => p.name).join(', ')}
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Tooltip title={getAuthLabel(endpoint.auth)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getAuthIcon(endpoint.auth)}
                                  <Typography variant="caption">{getAuthLabel(endpoint.auth)}</Typography>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip title={copiedPath === group.basePath + endpoint.path ? t('configApi.copied') : t('configApi.copyUrl')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyPath(group.basePath + endpoint.path)}
                                >
                                  {copiedPath === group.basePath + endpoint.path ? (
                                    <CheckCircle fontSize="small" color="success" />
                                  ) : (
                                    <ContentCopy fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
        </Grid>

        {/* Usage Examples */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('configApi.usageExamples')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('configApi.authentication')}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: '#111827',
                        color: '#E5E7EB',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: 12,
                      }}
                    >
{`curl -X POST ${baseUrl}/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com","password":"***"}'`}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('configApi.requestWithToken')}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: '#111827',
                        color: '#E5E7EB',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: 12,
                      }}
                    >
{`curl -X GET ${baseUrl}/v1/admin/services \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json"`}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Response Format */}
        <Grid size={{ xs: 12 }}>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('configApi.responseFormat')}
            </Typography>
            <Typography variant="body2">
              {t('configApi.responseFormatDesc')}{' '}
              <code>{'{ success: boolean, data?: any, error?: string, pagination?: {...} }'}</code>
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiDocumentation;
